import { useState } from 'react';
import type { AuctionDetail } from '@/types/domain';
import { formatMoney } from '@/lib/format';
import { kopecksToRublesInput, parseRublesToKopecks, sanitizeRublesInput } from '@/lib/moneyInput';
import {
  computeMinRequiredBid,
  formatAutoBidLimitsHint,
  formatMaxBidExceededError,
  getMaxAutoBidAmount,
  MAX_BID_STEP,
} from '@/lib/bidLimits';
import { Button } from '@/components/ui/Button';
import { useSetAutoBid } from '@/hooks/useAuctions';
import { haptic } from '@/lib/telegram';
import clsx from 'clsx';
import styles from './AutoBidSheet.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  auction: AuctionDetail;
}

export const AutoBidSheet = ({ isOpen, onClose, auction }: Props) => {
  const minMax = computeMinRequiredBid(auction);
  const maxAllowed = getMaxAutoBidAmount(auction);
  const canSetAutoBid =
    auction.buy_now_price == null || minMax <= auction.buy_now_price;
  const defaultMax = canSetAutoBid
    ? Math.min(minMax * 2, maxAllowed)
    : maxAllowed;
  const [maxInput, setMaxInput] = useState(kopecksToRublesInput(defaultMax));
  const [stepAmount, setStepAmount] = useState<number | null>(null);
  const [stepInput, setStepInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setAutoBid = useSetAutoBid(auction.id);

  const handleSubmit = async () => {
    if (!canSetAutoBid) return;

    setError(null);

    const parsedMax = parseRublesToKopecks(maxInput);
    if (parsedMax === null) {
      setError('Введи максимальную сумму');
      return;
    }
    if (parsedMax < minMax) {
      setError(`Минимум ${formatMoney(minMax)}`);
      return;
    }
    if (parsedMax > maxAllowed) {
      setError(
        formatMaxBidExceededError(
          {
            bids_count: auction.bids_count,
            start_price: auction.start_price,
            current_price: auction.current_price,
            min_step: auction.min_step,
            buy_now_price: auction.buy_now_price,
          },
          parsedMax,
        ),
      );
      return;
    }
    if (stepAmount !== null && stepAmount > MAX_BID_STEP) {
      setError(`Максимальный шаг — ${formatMoney(MAX_BID_STEP)}`);
      return;
    }

    try {
      await setAutoBid.mutateAsync({ maxAmount: parsedMax, stepAmount });
      haptic.success();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка';
      setError(msg);
      haptic.error();
    }
  };

  return (
    <div className={clsx(styles.overlay, isOpen && styles.overlayOpen)} onClick={onClose}>
      <div className={clsx(styles.sheet, isOpen && styles.sheetOpen)} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle} />

        <div className={styles.header}>
          <p className={styles.title}>Автоставка</p>
          <p className={styles.subtitle}>
            Система будет автоматически перебивать ставки других участников,
            пока не дойдёт до твоего максимума.
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Максимальная сумма</label>
          <div className={styles.inputWrap}>
            <input
              type="text"
              value={maxInput}
              onChange={(e) => {
                const sanitized = sanitizeRublesInput(e.target.value);
                setMaxInput(sanitized);
                setError(null);
              }}
              className={styles.input}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <span className={styles.currency}>₽</span>
          </div>
          <p className={styles.hint}>
            {formatAutoBidLimitsHint(auction)}
            {auction.bids_count === 0 && canSetAutoBid ? ' · первая ставка от стартовой' : ''}
          </p>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Шаг автоставки (опц.)</label>
          <div className={styles.inputWrap}>
            <input
              type="text"
              value={stepInput}
              onChange={(e) => {
                const sanitized = sanitizeRublesInput(e.target.value);
                setStepInput(sanitized);
                setStepAmount(parseRublesToKopecks(sanitized));
              }}
              placeholder={String(auction.min_step / 100)}
              className={styles.input}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            <span className={styles.currency}>₽</span>
          </div>
          <p className={styles.hint}>
            По умолчанию — шаг аукциона ({formatMoney(auction.min_step)}), максимум {formatMoney(MAX_BID_STEP)}
          </p>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} fullWidth>
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={setAutoBid.isPending}
            disabled={!canSetAutoBid}
            fullWidth
          >
            Включить
          </Button>
        </div>
      </div>
    </div>
  );
};